import React, { useEffect, useState, Suspense } from 'react';
import { useServiceWorkerUpdate } from '@/hooks/useServiceWorkerUpdate';
import { Capacitor } from '@capacitor/core';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import PreAuthWelcome from "./components/onboarding/PreAuthWelcome";
import { supabase } from "@/integrations/supabase/client";
import { ErrorBoundary } from "./components/util/ErrorBoundary";
import { GlobalErrorBoundary } from "./components/util/GlobalErrorBoundary";
import { AppLoader } from "./components/util/AppLoader";

import LandingPage from "./pages/LandingPage";
import Beta from "./pages/Beta";
import ElevenLabsGrant from "./pages/ElevenLabsGrant";
import GooglePlayAccelerator from "./pages/GooglePlayAccelerator";
import Dashboard from "./pages/Dashboard";
import TodayDashboard from "./pages/TodayDashboard";
import AllTools from "./pages/AllTools";
import ConversationalOnboarding from "./components/onboarding/ConversationalOnboarding";
import { OnboardingCheck } from "./components/OnboardingCheck";
import Therapy from "./pages/Therapy";
import Sanctuary from "./pages/Sanctuary";
import Manifestation from "./pages/Manifestation";
import DailyCheckInPage from "./pages/DailyCheckInPage";
import Rituals from "./pages/Rituals";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import NotFound from "./pages/NotFound";
import Courses from "./pages/Courses";
import CourseLibrary from "./pages/CourseLibrary";
import Meditations from "./pages/Meditations";
import MeditationLibrary from "./pages/MeditationLibrary";
import TelempathyTools from "./pages/TelempathyTools";
import ConnectAndGrow from "./pages/ConnectAndGrow";
import Vault from "./pages/Vault";
import AdminPortal from "./pages/AdminPortal";
import Admin from "./pages/Admin";
import SchoolAdmin from "./pages/SchoolAdmin";
import BusinessPortal from "./pages/BusinessPortal";
import ProtectedRoute from "./components/ProtectedRoute";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { ContentProvider } from "./contexts/ContentContext";
import { EditModeProvider } from "./contexts/EditModeContext";
import { LogoEditorProvider } from "./contexts/LogoEditorContext";
import { LogoEditorProvider as LogoEditorControlsProvider } from "./components/LogoEditor";
import { WhoopSyncProvider } from "./components/providers/WhoopSyncProvider";
import Subscription from "./pages/Subscription";
import Account from "./pages/Account";
import Credits from "./pages/Credits";
import Settings from "./pages/Settings";
import CrisisPage from "./pages/CrisisPage";
import WhoopCallback from "./pages/WhoopCallback";
import WhoopDashboard from "./pages/WhoopDashboard";
import WhoopLogin from "./pages/WhoopLogin";
import WhoopRedirect from "./pages/WhoopRedirect";
import GarminCallback from "./pages/GarminCallback";
import GarminDashboard from "./pages/GarminDashboard";
import GarminLogin from "./pages/GarminLogin";
import MindBodyInsightsPage from "./pages/MindBodyInsights";
import FutureSelfNavigator from "./pages/FutureSelfNavigator";
import WellnessTracks from "./pages/WellnessTracks";
import InsightsV2Page from "./pages/InsightsV2Page";
import NotificationHistory from "./pages/NotificationHistory";
import TestNotifications from "./pages/TestNotifications";
import Longevity from "./pages/Longevity";
import InflammationHub from "./pages/InflammationHub";
import ProtocolDetail from "./pages/ProtocolDetail";
import MentalWellness from "./pages/MentalWellness";
import MyScores from "./pages/MyScores";
import HistoricalDataHub from "./pages/HistoricalDataHub";
import TrendsPage from "./pages/TrendsPage";
import ChallengeDetail from "./pages/ChallengeDetail";
import ThoughtTracker from "./pages/ThoughtTracker";
import MentalToolsHub from "./pages/MentalToolsHub";
import Achievements from "./pages/Achievements";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TourPreview from "./pages/TourPreview";
import TermsOfService from "./pages/TermsOfService";
import Terms from "./legal/pages/Terms";
import Privacy from "./legal/pages/Privacy";
import Subprocessors from "./pages/Subprocessors";
import AITransparency from "./pages/AITransparency";
import ConsumerHealthNotice from "./pages/ConsumerHealthNotice";
import Breathwork from "./pages/Breathwork";
import RockRemedies from "./pages/RockRemedies";
import RelationalHub from "./pages/RelationalHub";
import { usePushNotifications } from "./hooks/usePushNotifications";
import { useLocalNotifications } from "./hooks/useLocalNotifications";
import { useStatusBar } from "./hooks/useStatusBar";
import { TrialExpiryBanner } from "./components/TrialExpiryBanner";
import { CrisisModeBanner } from "./components/CrisisModeBanner";
import { CrisisExitPrompt } from "./components/CrisisExitPrompt";
import OnboardingWrapper from "./components/OnboardingWrapper";
import { FloatingGuideButton } from "./components/guide/FloatingGuideButton";
import { TherappiWelcomeHandler } from "./components/guide/TherappiWelcomeHandler";
import { TourContainer } from "./components/guide/TourContainer";
import { OnboardingDebugOverlay } from "./components/debug/OnboardingDebugOverlay";
import { FlightRecorderOverlay } from "./components/debug/FlightRecorderOverlay";
import { SkipToContent } from "./components/accessibility/SkipToContent";
import ClinicianPortal from "./pages/ClinicianPortal";
import ClinicianSales from "./pages/ClinicianSales";
import AcceptInvitation from "./pages/AcceptInvitation";
import ClientPortal from "./pages/ClientPortal";
import Subscribe from "./pages/Subscribe";

// Lazy-loaded heavy routes for code-splitting
const Nutrition = React.lazy(() => import('./pages/Nutrition'));
const SleepAndDreams = React.lazy(() => import('./pages/SleepAndDreams'));

import { 
  useOnboardingStateMachine,
  completePreAuthWelcome,
  completePreAuthTypewriter,
  logFlight
} from '@/hooks/useOnboardingStateMachine';
import { TherappiWelcome, getWelcomeContent } from './components/guide/TherappiWelcome';

const queryClient = new QueryClient();

/**
 * HomeRedirect - Unified onboarding gate using state machine as SINGLE SOURCE OF TRUTH
 * 
 * SEQUENCE (native):
 * 1. pre_auth_welcome → PreAuthWelcome (4-box) [native only]
 * 2. pre_auth_typewriter → TherappiWelcome typewriter intro [native only]
 * 3. auth → Auth page
 * 4. conversational → handled by OnboardingWrapper
 * 5. tour → handled by TourContainer
 * 6. app → TodayDashboard
 */
const HomeRedirect = () => {
  const isNative = Capacitor.isNativePlatform();
  const navigate = useNavigate();
  const { stage, isHydrated, userId } = useOnboardingStateMachine();
  
  const logHome = (event: string, data: Record<string, unknown> = {}) => {
    logFlight(`HOME_${event}`, { ...data, stage, isNative, isHydrated, hasUser: !!userId });
  };
  
  // CRITICAL: Hydration check MUST be FIRST - before any stage-based rendering
  // This prevents rendering pre-auth content while auth state is still loading
  if (stage === 'hydrating' || !isHydrated) {
    logHome('RENDER_HYDRATING', { stage, isHydrated });
    return (
      <div className="fixed inset-0 min-h-screen flex items-center justify-center bg-[hsl(182,29%,26%)] z-50">
        <div className="h-12 w-12 border-4 border-[hsl(177,41%,76%)] border-t-[hsl(182,37%,46%)] rounded-full animate-spin" />
      </div>
    );
  }
  
  // SAFETY: After hydration, NEVER show pre-auth screens if user exists
  // This is a backstop in case state machine has a bug
  if (userId) {
    // User is authenticated - only allow post-auth stages
    if (stage === 'pre_auth_welcome' || stage === 'pre_auth_typewriter') {
      logHome('BLOCKED_PRE_AUTH_FOR_AUTHED_USER', { stage, userId });
      // Force to conversational if onboarding not done, otherwise to app
      return <ConversationalOnboarding />;
    }
  }
  
  // Stage 1: 4-box Welcome (native only, unauthenticated)
  if (stage === 'pre_auth_welcome') {
    logHome('RENDER_PRE_AUTH_WELCOME');
    return (
      <PreAuthWelcome 
        onContinue={() => {
          logHome('PRE_AUTH_WELCOME_CONTINUE');
          completePreAuthWelcome();
        }} 
      />
    );
  }
  
  // Stage 2: Typewriter intro (native only, after 4-box welcome, unauthenticated)
  if (stage === 'pre_auth_typewriter') {
    logHome('RENDER_PRE_AUTH_TYPEWRITER');
    const content = getWelcomeContent('/dashboard');
    return (
      <TherappiWelcome
        pageKey="pre_auth_typewriter"
        title={content?.title ?? 'Welcome to Therappi'}
        message={content?.message ?? ''}
        tips={content?.tips ?? []}
        buttonText="Discover What Therappi® Offers"
        onDismiss={() => {
          logHome('PRE_AUTH_TYPEWRITER_CONTINUE');
          completePreAuthTypewriter();
        }}
      />
    );
  }
  
  // Stage 3: Auth (unauthenticated users)
  if (stage === 'auth') {
    if (isNative) {
      logHome('RENDER_AUTH_NATIVE');
      return <Auth />;
    }
    logHome('RENDER_LANDING_WEB');
    return <LandingPage />;
  }
  
  // Stage 4: Conversational - MUST render before tour
  // This is the authoritative gate - state machine determines stage from DB
  if (stage === 'conversational') {
    logHome('RENDER_CONVERSATIONAL');
    return <ConversationalOnboarding />;
  }
  
  // Stage 5: Tour - render TodayDashboard, TourContainer will overlay
  // Stage 6: App - render TodayDashboard normally
  logHome('RENDER_TODAY_DASHBOARD', { stage });
  return <TodayDashboard />;
};

const AppContent = () => {
  usePushNotifications();
  useLocalNotifications();
  useStatusBar();
  
  return (
    <Routes>
      {/* Onboarding Route - Conversational welcome flow */}
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <ConversationalOnboarding />
        </ProtectedRoute>
      } />
      
      {/* Landing page for unauthenticated visitors */}
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/beta" element={<Beta />} />
      
      {/* Main App Routes - Check onboarding status */}
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/elevenlabs-grant" element={<ElevenLabsGrant />} />
      <Route path="/google-play-accelerator" element={<GooglePlayAccelerator />} />
      <Route path="/inflammation" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <ErrorBoundary>
              <InflammationHub />
            </ErrorBoundary>
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      <Route path="/longevity" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <ErrorBoundary>
              <Longevity />
            </ErrorBoundary>
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      <Route path="/longevity/inflammation" element={
        <ProtectedRoute>
          <ErrorBoundary>
            <InflammationHub />
          </ErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/longevity/protocol/:id" element={
        <ProtectedRoute>
          <ErrorBoundary>
            <ProtocolDetail />
          </ErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/auth" element={<Auth />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* Tour Preview - isolated test route that bypasses all guards */}
      <Route path="/tour-preview" element={<TourPreview />} />
      <Route path="/whoop" element={<WhoopLogin />} />
      <Route path="/whoop-login" element={<WhoopLogin />} />
      <Route path="/garmin-login" element={<GarminLogin />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/legal/terms" element={<Terms />} />
      <Route path="/legal/privacy" element={<Privacy />} />
      <Route path="/subprocessors" element={<Subprocessors />} />
      <Route path="/ai-transparency" element={<AITransparency />} />
      <Route path="/consumer-health-notice" element={<ConsumerHealthNotice />} />
      {/* Subscribe page - protected, not public yet */}
      <Route path="/subscribe" element={
        <ProtectedRoute>
          <Subscribe />
        </ProtectedRoute>
      } />
      <Route path="/breathwork" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <Breathwork />
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      <Route path="/relational" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <RelationalHub />
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      {/* Dashboard redirect - /dashboard now redirects to home */}
      <Route path="/dashboard" element={<Navigate to="/" replace />} />
      {/* Legacy Dashboard - archived, kept for reference */}
      <Route path="/dashboard-v1" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/therapy" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <Therapy />
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      <Route path="/sanctuary" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <Sanctuary />
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      <Route path="/manifestation" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <Manifestation />
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      <Route path="/telempathy-tools" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <TelempathyTools />
          </OnboardingCheck>
        </ProtectedRoute>
      } />
          <Route path="/sleep-and-dreams" element={
            <ProtectedRoute>
              <OnboardingCheck>
                <Suspense fallback={<AppLoader text="Loading..." />}>
                  <SleepAndDreams />
                </Suspense>
              </OnboardingCheck>
            </ProtectedRoute>
          } />
          <Route path="/vault" element={
            <ProtectedRoute>
              <OnboardingCheck>
                <Vault />
              </OnboardingCheck>
            </ProtectedRoute>
          } />
      <Route path="/connect-and-grow" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <ConnectAndGrow />
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      <Route path="/community" element={<Navigate to="/connect-and-grow" replace />} />
      <Route path="/rituals" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <Rituals />
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      <Route path="/daily-check-in" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <DailyCheckInPage />
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      <Route path="/daily-checkin" element={<Navigate to="/daily-check-in" replace />} />
      <Route path="/trends" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <TrendsPage />
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      <Route path="/historical-data" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <HistoricalDataHub />
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      <Route path="/mental-wellness" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <ErrorBoundary>
              <MentalWellness />
            </ErrorBoundary>
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      <Route path="/thought-tracker" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <ErrorBoundary>
              <ThoughtTracker />
            </ErrorBoundary>
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      <Route path="/mental-tools" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <ErrorBoundary>
              <MentalToolsHub />
            </ErrorBoundary>
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      <Route path="/my-scores" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <ErrorBoundary>
              <MyScores />
            </ErrorBoundary>
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      <Route path="/correlation-explorer" element={
        <Navigate to="/vault?tab=correlations" replace />
      } />
      <Route path="/rock-remedies" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <RockRemedies />
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      <Route path="/all-tools" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <AllTools />
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      <Route path="/dashboard-v1" element={
        <Navigate to="/all-tools" replace />
      } />
      <Route path="/future-self-navigator" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <FutureSelfNavigator />
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      <Route path="/future-self" element={<Navigate to="/future-self-navigator" replace />} />
      <Route path="/future-self-spotlight" element={<Navigate to="/future-self-navigator" replace />} />
      <Route path="/subscription" element={
        <ProtectedRoute>
          <Subscription />
        </ProtectedRoute>
      } />
      <Route path="/account" element={
        <ProtectedRoute>
          <Account />
        </ProtectedRoute>
      } />
      <Route path="/crisis" element={
        <ProtectedRoute>
          <CrisisPage />
        </ProtectedRoute>
      } />
      <Route path="/courses" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <Courses />
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      <Route path="/course-library" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <CourseLibrary />
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      <Route path="/meditations" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <Meditations />
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      <Route path="/meditation-library" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <MeditationLibrary />
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <Admin />
        </ProtectedRoute>
      } />
      <Route path="/admin-portal" element={
        <ProtectedRoute>
          <AdminPortal />
        </ProtectedRoute>
      } />
      <Route path="/school-admin" element={
        <ProtectedRoute>
          <SchoolAdmin />
        </ProtectedRoute>
      } />
      <Route path="/business" element={
        <ProtectedRoute>
          <BusinessPortal />
        </ProtectedRoute>
      } />
      <Route path="/credits" element={
        <ProtectedRoute>
          <Credits />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      } />
      <Route path="/whoop-callback" element={
        <ProtectedRoute>
          <WhoopCallback />
        </ProtectedRoute>
      } />
      <Route path="/whoop-dashboard" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <WhoopDashboard />
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      <Route path="/garmin-callback" element={
        <ProtectedRoute>
          <GarminCallback />
        </ProtectedRoute>
      } />
      <Route path="/garmin-demo" element={
        <ProtectedRoute>
          <GarminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/mind-body-insights" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <MindBodyInsightsPage />
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      <Route path="/nutrition" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <Suspense fallback={<AppLoader text="Loading Nutrition..." />}>
              <Nutrition />
            </Suspense>
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      <Route path="/wellness-tracks" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <WellnessTracks />
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      
      {/* PHASE 3: AI Insights Engine v2 */}
      <Route path="/insights" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <InsightsV2Page />
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      
      {/* Notification History */}
      <Route path="/notifications" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <NotificationHistory />
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      
      {/* Challenge Detail */}
      <Route path="/challenge/:challengeId" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <ChallengeDetail />
          </OnboardingCheck>
        </ProtectedRoute>
      } />

      {/* Achievements Gallery */}
      <Route path="/achievements" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <Achievements />
          </OnboardingCheck>
        </ProtectedRoute>
      } />

      {/* Clinician Portal */}
      <Route path="/clinician" element={
        <ProtectedRoute>
          <ClinicianPortal />
        </ProtectedRoute>
      } />
      <Route path="/clinician-portal" element={
        <ProtectedRoute>
          <ClinicianPortal />
        </ProtectedRoute>
      } />
      <Route path="/clinician-sales" element={<ClinicianSales />} />
      <Route path="/for-therapists" element={<ClinicianSales />} />
      <Route path="/accept-invitation" element={<AcceptInvitation />} />
      <Route path="/client-portal" element={
        <ProtectedRoute>
          <ClientPortal />
        </ProtectedRoute>
      } />
      <Route path="/my-therapist" element={
        <ProtectedRoute>
          <ClientPortal />
        </ProtectedRoute>
      } />

      {/* Test Notifications */}
      <Route path="/test-notifications" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <TestNotifications />
          </OnboardingCheck>
        </ProtectedRoute>
      } />

      {/* Journal, Exercise, Goals, Lunar/Cycle, Appointments */}
      <Route path="/journal" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <React.Suspense fallback={<div>Loading...</div>}>
              {React.createElement(React.lazy(() => import('./pages/Journal')))}
            </React.Suspense>
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      <Route path="/exercise" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <React.Suspense fallback={<div>Loading...</div>}>
              {React.createElement(React.lazy(() => import('./pages/Exercise')))}
            </React.Suspense>
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      <Route path="/goals" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <React.Suspense fallback={<div>Loading...</div>}>
              {React.createElement(React.lazy(() => import('./pages/Goals')))}
            </React.Suspense>
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      <Route path="/lunar-cycle" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <React.Suspense fallback={<div>Loading...</div>}>
              {React.createElement(React.lazy(() => import('./pages/LunarCycle')))}
            </React.Suspense>
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      <Route path="/appointments" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <React.Suspense fallback={<div>Loading...</div>}>
              {React.createElement(React.lazy(() => import('./pages/Appointments')))}
            </React.Suspense>
          </OnboardingCheck>
        </ProtectedRoute>
      } />

      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

// Inner component rendered inside BrowserRouter so hooks that need router context work safely
const AppShellInner: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useServiceWorkerUpdate();
  return <>{children}</>;
};

const App = () => {
  // Web/PWA: register the service worker.
  // Native (Capacitor): disable service workers + clear caches to avoid stale UI bundles.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const isNative = !!Capacitor?.isNativePlatform?.();

    const clearServiceWorkersAndCaches = async () => {
      try {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      } catch {
        // ignore
      }

      if ('caches' in window) {
        try {
          const names = await caches.keys();
          await Promise.all(names.map((n) => caches.delete(n)));
        } catch {
          // ignore
        }
      }
    };

    if (isNative) {
      void clearServiceWorkersAndCaches();
      return;
    }

    // Web/PWA only: cleanup older therappi caches
    if ('caches' in window) {
    const buildVersion = '2026-02-17';
      const keep = new Set([`therappi-v4-${buildVersion}`, `therappi-static-v4-${buildVersion}`, `therappi-dynamic-v4-${buildVersion}`]);
      caches.keys().then((names) => {
        names.forEach((name) => {
          if (name.startsWith('therappi-') && !keep.has(name)) {
            caches.delete(name);
          }
        });
      });
    }

    navigator.serviceWorker
      .register('/sw.js', { updateViaCache: 'none' })
      .then((registration) => {
        registration.update();
      })
      .catch(() => {
        // Service worker registration failed - handled silently
      });

    // Listen for SW_UPDATED message to show toast
    const handleSWMessage = (event: MessageEvent) => {
      if (event.data?.type === 'SW_UPDATED') {
        // Dispatch custom event that the toast system can pick up
        window.dispatchEvent(new CustomEvent('therappi-sw-updated'));
      }
    };
    navigator.serviceWorker.addEventListener('message', handleSWMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleSWMessage);
    };
  }, []);

  return (
    <GlobalErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ContentProvider>
          <EditModeProvider>
            <SubscriptionProvider>
              <LogoEditorProvider>
                <LogoEditorControlsProvider>
                  <TooltipProvider>
                    <WhoopSyncProvider>
                      <Toaster />
                      <SonnerToaster />
                      <BrowserRouter>
                        <AppShellInner>
                          <SkipToContent />
                          
                          <OnboardingWrapper>
                            <TrialExpiryBanner />
                            <CrisisModeBanner />
                            <CrisisExitPrompt />
                            <FloatingGuideButton />
                            <TherappiWelcomeHandler />
                            {/* Tour mounted at App shell level - won't unmount on route changes */}
                            <TourContainer />
                            <OnboardingDebugOverlay />
                            <FlightRecorderOverlay />
                            <main id="main-content">
                              <AppContent />
                            </main>
                          </OnboardingWrapper>
                        </AppShellInner>
                      </BrowserRouter>
                    </WhoopSyncProvider>
                  </TooltipProvider>
                </LogoEditorControlsProvider>
              </LogoEditorProvider>
            </SubscriptionProvider>
          </EditModeProvider>
        </ContentProvider>
      </QueryClientProvider>
    </GlobalErrorBoundary>
  );
};

export default App;
